import { P2GBox, P2GText } from '@parcel2go/core-ui';
import { ProviderGroup } from '../../../../lib/helpers/SmartSendBulkActionApplier';
import { FixedColumn } from '../../../FixedColumn';
import { GroupHasDuties, GroupHasVat } from './HasVat';
import { toDisplayCurrency } from '~/components/currency/toDisplayCurrency';
import { TranslationNamespaces } from '~/lib/constants';
import { Trans, useTranslation } from 'react-i18next';
import { useTaxName } from '@parcel2go/npm-p2g-app';

interface props {
  group: ProviderGroup;
}

const ProviderGroupSummaryTotalFallback: React.FC<props> = (props) => {
  const { t } = useTranslation(TranslationNamespaces.SMARTSEND);
  const discountApplied = props.group.Discount?.TotalDiscount > 0;
  const groupHasVat = GroupHasVat(props.group);
  const groupHasDuties = GroupHasDuties(props.group);
  const taxName = useTaxName();

  return (
    <P2GBox sx={(t) => ({ borderRadius: t.spacing(1), overflow: 'hidden' })}>
      {(groupHasVat || discountApplied || groupHasDuties) && (
        <P2GBox p={2} sx={(t) => ({ background: t.palette.grey.A100 })}>
          <FixedColumn
            left={
              <P2GText variant="body2">{t('PROVIDER_GROUP_SUMMARY_SUBTOTAL', { defaultValue: 'Sub Total:' })}</P2GText>
            }
            right={
              <P2GBox textAlign="right">
                <P2GText variant="body2">
                  {discountApplied ? (
                    <>
                      {toDisplayCurrency(props.group.CurrencyCode, props.group.Discount.TotalPriceExVat)}
                      &nbsp;
                      <s>{toDisplayCurrency(props.group.CurrencyCode, props.group.TotalPriceExVat)}</s>
                    </>
                  ) : (
                    toDisplayCurrency(props.group.CurrencyCode, props.group.TotalPriceExVat)
                  )}
                </P2GText>
              </P2GBox>
            }
            width={undefined}
          />
          {groupHasVat && (
            <FixedColumn
              left={
                <P2GText variant="body2">
                  {t(`PROVIDER_GROUP_SUMMARY_TAX`, { defaultValue: `{{taxName}}:`, taxName: taxName })}
                </P2GText>
              }
              right={
                <P2GBox textAlign="right">
                  <P2GText variant="body2" disableSmallUnit>
                    {toDisplayCurrency(
                      props.group.CurrencyCode,
                      discountApplied ? props.group.Discount.TotalVat : props.group.TotalVat
                    )}
                  </P2GText>
                </P2GBox>
              }
              width={undefined}
            />
          )}
          {groupHasDuties && (
            <FixedColumn
              left={
                <P2GText variant="body2">
                  {t(`PROVIDER_GROUP_SUMMARY_DUTIES`, { defaultValue: `Duties and Taxes:` })}
                </P2GText>
              }
              right={
                <P2GBox textAlign="right">
                  <P2GText variant="body2" disableSmallUnit>
                    {toDisplayCurrency(props.group.CurrencyCode, props.group.Duties)}
                  </P2GText>
                </P2GBox>
              }
              width={undefined}
            />
          )}
        </P2GBox>
      )}
      <P2GBox p={2} sx={(t) => ({ background: t.palette.grey.A200 })}>
        <FixedColumn
          left={
            <P2GText variant="body2">
              <strong>
                <Trans t={t} i18nKey="PROVIDER_GROUP_SUMMARY_TOTAL" defaults="Total:" />
              </strong>
            </P2GText>
          }
          right={
            <P2GBox textAlign="right">
              <P2GText variant="body2">
                {toDisplayCurrency(
                  props.group.CurrencyCode,
                  discountApplied ? props.group.Discount.TotalPrice : props.group.TotalPrice
                )}
              </P2GText>
            </P2GBox>
          }
          width={undefined}
        />
      </P2GBox>
    </P2GBox>
  );
};

export default ProviderGroupSummaryTotalFallback;
